import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import 'iconify-icon';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Cases from '../components/Cases';
import { useGlobalWaLink } from '../contexts/SettingsContext';

const STATS = [
  { value: '15+', label: 'anos de atuação' },
  { value: '3.000+', label: 'empresas atendidas' },
  { value: '70.000+', label: 'eneagramados' },
  { value: 'NPS 95%+', label: 'satisfação' },
  { value: '100%', label: 'metodologia patenteada' },
];

const PROGRAMS = [
  {
    icon: 'solar:user-circle-linear',
    slug: 'e-personalidade',
    name: 'E-Personalidade In Company',
    tag: 'Diagnóstico',
    tagline: 'Programa de desenvolvimento emocional e comportamental para equipes.',
    description:
      'Diagnóstico patenteado dos 27 padrões comportamentais. Cada colaborador recebe um relatório individual + plano de governança. Base obrigatória para qualquer intervenção subsequente.',
    competencias: [
      'Autoconsciência emocional',
      'Gestão de conflitos',
      'Tomada de decisão técnica',
      'Comunicação assertiva',
      'Liderança com autoridade',
    ],
    format: 'Extensivo 32h ou Intensivo 22h',
  },
  {
    icon: 'solar:graph-up-linear',
    slug: 'e-processual',
    name: 'E-Processual In Company',
    tag: 'Processos & Pessoas',
    tagline: 'Intervenção na gestão de projetos e ciclos organizacionais.',
    description:
      'Engenharia comportamental aplicada ao fluxo operacional. Aloca pessoas certas nos pontos certos do processo, reduzindo retrabalho e aumentando previsibilidade de entrega.',
    competencias: [
      'Leitura de cenários',
      'Condução técnica de equipes',
      'Delegação por perfil',
      'Previsibilidade de entrega',
    ],
    format: 'Extensivo 12 encontros',
  },
  {
    icon: 'solar:dumbbell-large-linear',
    slug: 'e-corpo',
    name: 'E-Corpo In Company',
    tag: 'Saúde & Performance',
    tagline: 'Diagnóstico e intervenção nos padrões corporais da equipe.',
    description:
      'Programa somático corporativo. Atua na dimensão corporal dos padrões comportamentais — destrava performance, previne burnout e instala presença executiva.',
    competencias: [
      'Presença e autoridade corporal',
      'Leitura corporal técnica',
      'Neutralização de mecanismos de defesa',
      'Indicador de bem-estar mensurável',
    ],
    format: 'Programa modular customizado',
  },
  {
    icon: 'solar:cart-large-2-linear',
    slug: 'e-vendas',
    name: 'E-Vendas In Company',
    tag: 'Comercial',
    tagline: 'Evolução da performance comercial por perfil.',
    description:
      'Time comercial calibrado por perfil de cliente. Conversão sobe sem aumentar o pipeline. Negociação consciente e governança de funil aplicadas com método.',
    competencias: [
      'Leitura comportamental do cliente',
      'Negociação consciente',
      'Governança do pipeline',
      'Aumento mensurável de conversão',
    ],
    format: 'Extensivo 36h ou Intensivo 22h',
  },
  {
    icon: 'solar:crown-line-linear',
    slug: 'e-got',
    name: 'E-GOT In Company',
    tag: 'Alta Liderança',
    tagline: 'Laboratório comportamental para C-level e sucessores.',
    description:
      'Imersão experiencial de alta intensidade. Cenários de poder, conflito e alianças aplicados à governança corporativa real. Reservado a turmas de 12-30 líderes.',
    competencias: [
      'Governança sob pressão',
      'Inteligência decisória',
      'Formação de alianças',
      'Simulações de poder real',
    ],
    format: 'Imersão de 1-2 dias · 12 a 30 líderes',
  },
];

const RESULTS = [
  {
    icon: 'solar:eye-linear',
    title: 'Clareza sobre padrões de funcionamento',
    text: 'Cada equipe entende objetivamente como funciona — sem achismo, sem psicologismo.',
  },
  {
    icon: 'solar:hand-shake-linear',
    title: 'Redução estrutural de conflitos',
    text: 'Leitura objetiva de motivações elimina atrito gerado por interpretações erradas.',
  },
  {
    icon: 'solar:rocket-2-linear',
    title: 'Aumento da eficiência relacional',
    text: 'Comunicação calibrada por perfil acelera decisões e desbloqueia execução.',
  },
  {
    icon: 'solar:shield-check-linear',
    title: 'Intervenção nos vícios emocionais',
    text: 'Padrões que distorcem entrega são tratados antes de virarem crise operacional.',
  },
];

const SolutionsCorporate = () => {
  const WA_LINK = useGlobalWaLink();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="Soluções In Company — Instituto Eneagrama"
        description="Engenharia comportamental aplicada à sua organização. Programas IE In Company: E-Personalidade, E-Processual, E-Corpo, E-Vendas, E-GOT."
        path="/solucoes-empresariais"
      />
      <Header />
      <main className="min-h-screen bg-black" data-testid="solutions-corporate-page">
        {/* HERO */}
        <section
          className="relative pt-32 pb-20 border-b border-neutral-900"
          style={{ background: 'rgba(10, 10, 10, 0.78)' }}
          data-testid="incompany-hero"
        >
          <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center">
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-4 block">
              Soluções Corporativas
            </span>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-[#EAEAEA] mb-5 leading-[1.05]">
              Engenharia Comportamental para a sua{' '}
              <span className="text-accent-orange">Organização</span>
            </h1>
            <p className="text-lg md:text-xl text-neutral-400 max-w-3xl mx-auto leading-[1.7] mb-10">
              Programas de intervenção direta nos padrões automáticos que travam produtividade,
              comunicação e resultados.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="incompany-hero-cta-proposal"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent-orange text-white rounded-full text-sm font-bold uppercase tracking-wider hover:bg-[#E55D00] transition-colors shadow-lg shadow-accent-orange/20"
              >
                <iconify-icon icon="solar:document-text-linear" className="text-lg"></iconify-icon>
                Solicitar proposta
              </a>
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="incompany-hero-cta-consultant"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#333333] text-[#EAEAEA] rounded-full text-sm font-bold uppercase tracking-wider hover:border-accent-orange hover:text-accent-orange transition-colors"
              >
                <iconify-icon icon="solar:chat-round-line-linear" className="text-lg"></iconify-icon>
                Falar com consultor
              </a>
            </div>
          </div>
        </section>

        {/* AUTHORITY NUMBERS */}
        <section
          className="relative z-10 py-12 border-b border-neutral-900"
          style={{ background: 'rgba(17, 17, 17, 0.95)' }}
          data-testid="incompany-authority"
        >
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-4 text-center">
              {STATS.map((s) => (
                <div key={s.label} className="border-r last:border-r-0 md:border-neutral-800 md:pr-4 last:pr-0">
                  <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-accent-orange mb-1.5">
                    {s.value}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 leading-tight">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PROGRAMS */}
        <section
          className="relative z-10 py-24 border-b border-neutral-900"
          style={{ background: 'rgba(10, 10, 10, 0.78)' }}
        >
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-3 block">
                5 programas modulares
              </span>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-[#EAEAEA] mb-4">
                Diagnóstico antes da intervenção
              </h2>
              <p className="text-base md:text-lg text-neutral-400 leading-[1.7]">
                Toda jornada IE começa pelo E-Personalidade. Os demais programas se conectam de
                acordo com o que sua organização precisa governar.
              </p>
            </div>

            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
              data-testid="incompany-programs-grid"
            >
              {PROGRAMS.map((p, i) => (
                <article
                  key={p.name}
                  data-testid={`incompany-program-${p.slug}`}
                  className="bg-[#0F0F0F] border border-[#262626] rounded-2xl p-7 md:p-8 hover:border-accent-orange/60 transition-colors flex flex-col"
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-14 h-14 rounded-full bg-[#1F1F1F] border border-[#333333] flex items-center justify-center text-accent-orange">
                      <iconify-icon icon={p.icon} className="text-2xl"></iconify-icon>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 block">
                        0{i + 1} / 05
                      </span>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-accent-orange/80">
                        {p.tag}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-[#EAEAEA] mb-2 leading-tight">
                    {p.name}
                  </h3>
                  <p className="text-sm font-medium text-neutral-300 mb-3 leading-snug">
                    {p.tagline}
                  </p>
                  <p className="text-sm text-neutral-500 mb-5 leading-[1.7] flex-1">
                    {p.description}
                  </p>

                  <div className="mb-5">
                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-accent-orange mb-2.5">
                      Competências desenvolvidas
                    </div>
                    <ul className="space-y-2">
                      {p.competencias.map((b) => (
                        <li
                          key={b}
                          className="flex items-start gap-2.5 text-xs text-neutral-300 leading-snug"
                        >
                          <iconify-icon
                            icon="solar:check-circle-bold"
                            className="text-accent-orange text-base flex-shrink-0 mt-0.5"
                          ></iconify-icon>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-5 pb-5 border-b border-[#262626]">
                    <iconify-icon icon="solar:clock-circle-linear" className="text-accent-orange"></iconify-icon>
                    {p.format}
                  </div>

                  <Link
                    to={`/${p.slug}`}
                    data-testid={`incompany-program-link-${p.slug}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-accent-orange hover:gap-2.5 transition-all"
                  >
                    Saiba mais
                    <iconify-icon icon="solar:arrow-right-linear"></iconify-icon>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* RESULTADOS ESPERADOS */}
        <section
          className="relative z-10 py-24 border-b border-neutral-900"
          style={{ background: 'rgba(17, 17, 17, 0.85)' }}
          data-testid="incompany-results"
        >
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-14 max-w-3xl mx-auto">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-3 block">
                Resultados esperados
              </span>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-[#EAEAEA] mb-4">
                O que muda na sua operação
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {RESULTS.map((r) => (
                <div
                  key={r.title}
                  className="bg-[#0F0F0F] border border-[#262626] rounded-2xl p-7 flex items-start gap-5 hover:border-accent-orange/40 transition-colors"
                >
                  <div className="w-12 h-12 rounded-full bg-accent-orange/15 border border-accent-orange/30 flex items-center justify-center text-accent-orange flex-shrink-0">
                    <iconify-icon icon={r.icon} className="text-2xl"></iconify-icon>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#EAEAEA] mb-2 leading-tight">
                      {r.title}
                    </h3>
                    <p className="text-sm text-neutral-400 leading-[1.7]">{r.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CASES — reaproveita o componente da home */}
        <Cases />

        {/* CTA FINAL */}
        <section
          className="relative z-10 py-24"
          style={{ background: 'rgba(10, 10, 10, 0.85)' }}
        >
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-4 block">
              Solicite uma proposta
            </span>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-[#EAEAEA] mb-4">
              Proposta <span className="text-accent-orange">personalizada</span> para sua empresa
            </h2>
            <p className="text-base md:text-lg text-neutral-400 mb-8 max-w-2xl mx-auto leading-[1.7]">
              Cada implementação é adaptada à realidade da sua organização. Fale com um consultor
              para diagnóstico inicial.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="incompany-final-cta-proposal"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent-orange text-white rounded-full text-sm font-bold uppercase tracking-wider hover:bg-[#E55D00] transition-colors shadow-lg shadow-accent-orange/20"
              >
                <iconify-icon icon="solar:document-text-linear" className="text-lg"></iconify-icon>
                Solicitar proposta
              </a>
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="incompany-final-cta-consultant"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#333333] text-[#EAEAEA] rounded-full text-sm font-bold uppercase tracking-wider hover:border-accent-orange hover:text-accent-orange transition-colors"
              >
                <iconify-icon icon="mdi:whatsapp" className="text-lg"></iconify-icon>
                Falar com consultor
              </a>
            </div>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-neutral-500">
              Condições especiais para turmas acima de 15 participantes
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default SolutionsCorporate;
